<template>
  <main class="lp">

    <!-- ── HEADER ── -->
    <header class="hdr">
      <div class="hdr__inner">
        <router-link class="hdr__logo" to="/" @click="closeMenu" aria-label="Fidmarket accueil">
          <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" class="hdr__logo-mark">
            <rect width="36" height="36" rx="10" fill="#1A3A2A"/>
            <rect x="7" y="12" width="22" height="14" rx="3" fill="#F5EDD6"/>
            <rect x="9" y="14" width="6" height="6" rx="1.5" fill="#2D6A4F"/>
            <rect x="17" y="14" width="10" height="2" rx="1" fill="#2D6A4F"/>
            <rect x="17" y="17" width="7" height="2" rx="1" fill="#2D6A4F"/>
            <circle cx="10" cy="28" r="2" fill="#E9C46A"/>
            <circle cx="16" cy="28" r="2" fill="#E9C46A"/>
            <circle cx="22" cy="28" r="2" fill="#E9C46A"/>
          </svg>
          <span class="hdr__logo-name">Fid<em>market</em></span>
        </router-link>

        <nav class="hdr__nav" :class="{ open: menuOpen }" id="nav">
          <a href="#s-sol" @click="closeMenu">La solution</a>
          <a href="#s-who" @click="closeMenu">Pour qui</a>
          <a href="#s-price" @click="closeMenu">Tarifs</a>
          <a href="#s-cta" @click="closeMenu">Contact</a>
        </nav>

        <div class="hdr__cta">
          <a href="#s-cta" class="btn btn-ghost" @click="closeMenu">Demander une démo</a>
          <router-link to="/login" class="btn btn-primary" @click="closeMenu">Accès commerçant</router-link>
        </div>

        <button class="hdr__burger" :class="{ open: menuOpen }" @click="menuOpen = !menuOpen"
          :aria-expanded="menuOpen.toString()" aria-controls="nav"
          :aria-label="menuOpen ? 'Fermer' : 'Menu'">
          <span/><span/><span/>
        </button>
      </div>
    </header>

    <!-- ── HERO ── -->
    <section class="hero" aria-label="Introduction">

      <!-- Bande de marché animée (signature) -->
      <div class="market-strip" aria-hidden="true">
        <div class="market-strip__track">
          <span v-for="(item, index) in doubledMarketItems" :key="item.id + '-' + index" class="market-strip__item">
            {{ item.emoji }} {{ item.label }}
          </span>
        </div>
      </div>

      <div class="hero__body">
        <div class="hero__left">
          <div class="hero__tag">
            <span class="tag-dot"></span>
            Fidélité pour commerçants de marché
          </div>

          <h1 class="hero__title">
            Vos clients<br/>
            <span class="hero__title-accent">reviennent.</span><br/>
            Vous le savez.
          </h1>

          <p class="hero__sub">
            Fidmarket donne une carte de fidélité numérique à chaque client,
            suit vos ventes et anticipe vos prochains marchés —
            le tout depuis une tablette, même sans connexion.
          </p>

          <div class="hero__actions">
            <a href="#s-cta" class="btn btn-primary btn-lg">Demander une démo gratuite</a>
            <a href="#s-sol" class="btn btn-soft btn-lg">Voir comment ça marche</a>
          </div>

          <div class="hero__proof">
            <div class="proof-item">
              <span class="proof-num">312</span>
              <span class="proof-label">clients fidélisés<br/>en moyenne</span>
            </div>
            <div class="proof-div"></div>
            <div class="proof-item">
              <span class="proof-num">+14%</span>
              <span class="proof-label">de CA constaté<br/>dès le 1er mois</span>
            </div>
            <div class="proof-div"></div>
            <div class="proof-item">
              <span class="proof-num">5 min</span>
              <span class="proof-label">pour prendre<br/>en main l'outil</span>
            </div>
          </div>
        </div>

        <!-- Mockup dashboard -->
        <div class="hero__mockup" aria-label="Aperçu du tableau de bord">
          <div class="mockup-card mockup-card--main">
            <div class="mockup-header">
              <div class="mockup-dots">
                <span class="dot dot--r"></span>
                <span class="dot dot--y"></span>
                <span class="dot dot--g"></span>
              </div>
              <span class="mockup-url">fidmarket.app</span>
            </div>
            <div class="mockup-body">
              <div class="mkpi-row">
                <div class="mkpi">
                  <div class="mkpi__label">CA aujourd'hui</div>
                  <div class="mkpi__val mkpi__val--green">1 248 €</div>
                  <div class="mkpi__trend">↑ +14%</div>
                </div>
                <div class="mkpi">
                  <div class="mkpi__label">Clients fidèles</div>
                  <div class="mkpi__val">312</div>
                  <div class="mkpi__trend mkpi__trend--neutral">+8 ce mois</div>
                </div>
                <div class="mkpi">
                  <div class="mkpi__label">Ventes</div>
                  <div class="mkpi__val">47</div>
                  <div class="mkpi__trend mkpi__trend--neutral">dont 29 avec carte</div>
                </div>
              </div>
              <div class="mforecast">
                <div class="mforecast__label">Prochain marché · Sam. Montpellier</div>
                <div class="mforecast__badge">🚀 Potentiel fort</div>
              </div>
              <div class="mclient-list">
                <div class="mclient" v-for="c in mockClients" :key="c.name">
                  <div class="mclient__avatar" :style="{ background: c.color }">{{ c.initials }}</div>
                  <div>
                    <div class="mclient__name">{{ c.name }}</div>
                    <div class="mclient__pts">{{ c.pts }} pts · {{ c.visits }} visites</div>
                  </div>
                  <div class="mclient__badge" :class="c.badgeClass">{{ c.badge }}</div>
                </div>
              </div>
            </div>
          </div>
          <!-- Carte fidélité flottante -->
          <div class="fid-card" aria-hidden="true">
            <div class="fid-card__brand">FIDmarket</div>
            <div class="fid-card__name">Marie D.</div>
            <div class="fid-card__dots">
              <span v-for="i in 5" :key="i" class="fid-dot" :class="{ filled: i <= 3 }"></span>
            </div>
            <div class="fid-card__sub">3 / 5 · Bon de 5€ à débloquer</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── PROBLÈME ── -->
    <section class="section section-problem" aria-labelledby="pb-title">
      <div class="wrap">
        <div class="section-eyebrow">Le constat</div>
        <h2 class="section-title" id="pb-title">
          Sur un marché, tout passe vite.<br/>
          <em>Même la fidélité.</em>
        </h2>

        <div class="problem-grid">
          <div class="problem-item" v-for="p in problems" :key="p.title">
            <div class="problem-item__icon">{{ p.icon }}</div>
            <h3 class="problem-item__title">{{ p.title }}</h3>
            <p class="problem-item__text">{{ p.text }}</p>
          </div>
        </div>

        <div class="problem-vs">
          <div class="vs-before">
            <div class="vs-label vs-label--before">Avant Fidmarket</div>
            <ul>
              <li>Cartes papier perdues ou oubliées</li>
              <li>CA recalculé à la main en fin de journée</li>
              <li>Aucune idée du stock à prévoir</li>
              <li>Clients fidèles… sans le savoir</li>
            </ul>
          </div>
          <div class="vs-arrow">→</div>
          <div class="vs-after">
            <div class="vs-label vs-label--after">Avec Fidmarket</div>
            <ul>
              <li>Carte numérique sur tablette, toujours là</li>
              <li>Clôture automatique en un tap</li>
              <li>Prévisions basées sur votre historique</li>
              <li>Chaque client reconnu, chaque visite comptée</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ── SOLUTION ── -->
    <section class="section section-solution" id="s-sol" aria-labelledby="sol-title">
      <div class="wrap">
        <div class="section-eyebrow section-eyebrow--light">La solution</div>
        <h2 class="section-title section-title--light" id="sol-title">
          Un seul outil.<br/>Tout votre marché.
        </h2>
        <p class="section-sub section-sub--light">
          Fidmarket centralise fidélité, ventes et prévisions dans une interface pensée pour être utilisée debout, entre deux clients, par n'importe quel temps.
        </p>

        <div class="features-list">
          <div class="feat" v-for="f in features" :key="f.title">
            <div class="feat__num">{{ f.num }}</div>
            <div class="feat__body">
              <div class="feat__icon">{{ f.icon }}</div>
              <h3 class="feat__title">{{ f.title }}</h3>
              <p class="feat__text">{{ f.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── POUR QUI ── -->
    <section class="section section-who" id="s-who" aria-labelledby="who-title">
      <div class="wrap">
        <div class="section-eyebrow">Pour qui ?</div>
        <h2 class="section-title" id="who-title">
          Du primeur au fromager.<br/>
          <em>Tous les étals.</em>
        </h2>

        <div class="etalage">
          <div class="etal" v-for="e in etals" :key="e.label">
            <span class="etal__emoji">{{ e.emoji }}</span>
            <span class="etal__label">{{ e.label }}</span>
          </div>
        </div>

        <blockquote class="temoignage">
          <p class="temoignage__text">
            "Avant je notais tout sur un cahier. Maintenant mes clients ont leur carte, je vois mes meilleurs marchés en un coup d'œil, et ma clôture prend 30 secondes."
          </p>
          <footer class="temoignage__footer">
            <span class="temoignage__name">Lorenzo Herrera</span>
            <span class="temoignage__role">Producteur bio & local · Marchés de Pézenas et La Grande-Motte</span>
          </footer>
        </blockquote>
      </div>
    </section>

    <!-- ── PRÉVISIONS ── -->
    <section class="section section-forecast" id="previsions" aria-labelledby="fc-title">
      <div class="wrap">
        <div class="section-eyebrow">Fonctionnalité clé</div>
        <h2 class="section-title" id="fc-title">
          Savoir avant d'y aller.<br/>
          <em>Préparer, pas improviser.</em>
        </h2>
        <p class="section-sub">
          Fidmarket analyse votre historique, les tendances, les jours fériés, les vacances scolaires et la météo pour vous donner une indication de potentiel avant chaque marché.
        </p>

        <div class="forecast-cards">
          <div
            v-for="card in forecastCards"
            :key="card.title"
            class="fc-card"
            :class="`fc-card--${card.level}`"
          >
            <div class="fc-card__indicator"></div>
            <div class="fc-card__tag">{{ card.tag }}</div>
            <div class="fc-card__icon">{{ card.icon }}</div>
            <h3 class="fc-card__title">{{ card.title }}</h3>
            <p class="fc-card__text">{{ card.text }}</p>
          </div>
        </div>
        <p class="forecast-disclaimer">Les prévisions sont indicatives et basées sur votre historique. Elles ne garantissent pas le chiffre d'affaires.</p>
      </div>
    </section>

    <!-- ── TARIFS ── -->
    <section class="section section-pricing" id="s-price" aria-labelledby="price-title">
      <div class="wrap">
        <div class="section-eyebrow">Tarifs</div>
        <h2 class="section-title section-title--center" id="price-title">Une offre. Tout compris.</h2>
        <p class="section-sub section-sub--center">
          Pas de surprise, pas de fonctionnalité cachée derrière un plan supérieur.
        </p>

        <div class="pricing-card">
          <div class="pricing-card__badge">🎯 Offre de lancement</div>
          <h3 class="pricing-card__title">Abonnement mensuel sans engagement</h3>
          <p class="pricing-card__desc">Accès complet à Fidmarket, accompagnement inclus, support réactif.</p>

          <ul class="pricing-features">
            <li v-for="f in pricingFeatures" :key="f">{{ f }}</li>
          </ul>

          <a href="#s-cta" class="btn btn-primary btn-lg btn-full">Recevoir un devis personnalisé</a>
          <p class="pricing-note">Tarif adapté à votre situation. Réponse sous 24h.</p>
        </div>
      </div>
    </section>

    <!-- ── CONTACT ── -->
    <section class="section section-contact" id="s-cta" aria-labelledby="cta-title">
      <div class="wrap">
        <div class="cta-block">
          <div class="cta-deco" aria-hidden="true">🛒</div>
          <h2 class="cta-title" id="cta-title">
            Votre prochain marché,<br/>
            avec Fidmarket.
          </h2>
          <p class="cta-sub">
            On vous présente l'outil en 20 minutes, à distance ou sur votre marché.
            Aucun engagement.
          </p>
          <div class="cta-actions">
            <a href="mailto:contact@fidmarket.fr" class="btn btn-gold btn-lg">Demander une démo</a>
            <router-link to="/login" class="btn btn-ghost-white btn-lg">Accès commerçant</router-link>
          </div>
          <div class="cta-contact-info">
            <a href="mailto:contact@fidmarket.fr">contact@fidmarket.fr</a>
            <span>·</span>
            <a href="tel:+33695267453">+33 6 95 26 74 53</a>
          </div>
        </div>
      </div>
    </section>

    <!-- ── FOOTER ── -->
    <footer class="footer" role="contentinfo">
      <div class="footer__inner">
        <div class="footer__brand">
          <div class="footer__brand-name">Fid<em>market</em></div>
          <p class="footer__brand-tag">Solution de fidélité numérique pour commerçants de marché. Simple, fiable, pensé terrain.</p>
        </div>
        <div>
          <div class="footer__col-title">Produit</div>
          <ul class="footer__links">
            <li><a href="#s-sol">Fonctionnalités</a></li>
            <li><a href="#previsions">Prévisions</a></li>
            <li><a href="#s-price">Tarifs</a></li>
            <li><router-link to="/login">Accès commerçant</router-link></li>
          </ul>
        </div>
        <div>
          <div class="footer__col-title">Entreprise</div>
          <ul class="footer__links">
            <li><a href="mailto:contact@fidmarket.fr">contact@fidmarket.fr</a></li>
            <li><router-link to="/mentions-legales">Mentions légales</router-link></li>
            <li><router-link to="/cgu">CGU</router-link></li>
            <li><router-link to="/confidentialite">Confidentialité</router-link></li>
          </ul>
        </div>
      </div>
      <div class="footer__bottom">
        <span>© 2026 Fidmarket. Tous droits réservés.</span>
        <nav aria-label="Liens légaux">
          <router-link to="/mentions-legales">Mentions légales</router-link>
          <router-link to="/confidentialite">Confidentialité</router-link>
          <a href="mailto:contact@fidmarket.fr">contact@fidmarket.fr</a>
        </nav>
      </div>
    </footer>

  </main>
</template>

<script>
export default {
  name: 'LandingView',
  data() {
    return {
      menuOpen: false,
      marketItems: [
        { id: 1,  emoji: '🍅', label: 'Tomates anciennes' },
        { id: 2,  emoji: '🧅', label: 'Oignons doux' },
        { id: 3,  emoji: '🧀', label: 'Fromage fermier' },
        { id: 4,  emoji: '🥖', label: 'Pain au levain' },
        { id: 5,  emoji: '🐟', label: 'Poisson frais' },
        { id: 6,  emoji: '🌿', label: 'Herbes aromatiques' },
        { id: 7,  emoji: '🍓', label: 'Fraises de saison' },
        { id: 8,  emoji: '🥚', label: 'Œufs fermiers' },
        { id: 9,  emoji: '🫙', label: 'Conserves maison' },
        { id: 10, emoji: '🧄', label: 'Ail rose' },
        { id: 11, emoji: '🌻', label: 'Fleurs coupées' },
        { id: 12, emoji: '🍯', label: 'Miel local' },
      ],
      mockClients: [
        { initials: 'MD', name: 'Marie D.',    pts: 8,  visits: 12, badge: 'VIP',        badgeClass: 'badge-vip',   color: '#2D6A4F' },
        { initials: 'PL', name: 'Pierre L.',   pts: 5,  visits: 7,  badge: 'Régulier',   badgeClass: 'badge-reg',   color: '#E9C46A' },
        { initials: 'SC', name: 'Sophie C.',   pts: 3,  visits: 4,  badge: 'Nouveau',    badgeClass: 'badge-new',   color: '#C77DFF' },
      ],
      problems: [
        { icon: '📋', title: "Les cartes papier s'envolent", text: 'Tampons oubliés, cartes perdues, clients frustrés. La fidélité papier ne tient pas à la réalité du marché.' },
        { icon: '🌫️', title: 'Le CA reste flou', text: "Entre les ventes avec carte et sans, recalculer tout à la main en fin de journée, c'est chronophage et source d'erreurs." },
        { icon: '🎲', title: "Stock à l'instinct", text: 'Pas de données sur les marchés passés ? Pas de prévision fiable. Résultat : trop ou pas assez, souvent au mauvais moment.' },
        { icon: '🔁', title: 'Aucun suivi de la fidélité réelle', text: "Savoir qui revient vraiment, à quelle fréquence, sur quel marché — sans outil, c'est impossible à suivre sérieusement." },
      ],
      features: [
        { num: '01', icon: '🪪', title: 'Carte de fidélité physique + numérique', text: 'Chaque client reçoit sa carte. QR code scannable, points cumulés automatiquement, bon de réduction généré dès le seuil atteint.' },
        { num: '02', icon: '🧾', title: 'Encaissement avec ou sans carte', text: 'Enregistrez toutes vos ventes en quelques taps. Avec carte ou anonyme, aucune vente ne passe à côté.' },
        { num: '03', icon: '📊', title: 'Suivi du CA par marché', text: "Visualisez votre chiffre d'affaires marché par marché, semaine par semaine. Identifiez vos meilleures dates." },
        { num: '04', icon: '✅', title: 'Clôture de journée en un tap', text: 'Fini les récapitulatifs manuels. Fidmarket génère votre bilan de journée automatiquement à la fermeture.' },
        { num: '05', icon: '🔮', title: 'Prévisions de stock intelligentes', text: 'Basées sur votre historique, la météo et les événements locaux. Un indicateur clair avant chaque marché.' },
        { num: '06', icon: '📶', title: 'Mode hors-ligne', text: 'Pas de connexion sur le marché ? Pas de problème. Fidmarket fonctionne offline et synchronise dès que le réseau revient.' },
      ],
      etals: [
        { emoji: '🍅', label: 'Primeur' },
        { emoji: '🧀', label: 'Fromager' },
        { emoji: '🐟', label: 'Poissonnier' },
        { emoji: '🥖', label: 'Boulanger' },
        { emoji: '🍱', label: 'Traiteur' },
        { emoji: '🌿', label: 'Producteur local' },
        { emoji: '👗', label: 'Textile' },
        { emoji: '🛒', label: 'Alimentaire' },
        { emoji: '🍯', label: 'Apiculteur' },
        { emoji: '🌻', label: 'Fleuriste' },
      ],
      forecastCards: [
        {
          level: 'low',
          tag: 'Tendance basse',
          icon: '⚠️',
          title: 'Réduire les stocks',
          text: 'Météo défavorable ou tendance récente en baisse. Évitez les invendus.',
        },
        {
          level: 'mid',
          tag: 'Potentiel moyen',
          icon: '📦',
          title: 'Stock habituel',
          text: 'Conditions proches de votre moyenne. Approvisionnement standard.',
        },
        {
          level: 'high',
          tag: 'Potentiel fort',
          icon: '🚀',
          title: 'Renforcez vos stocks',
          text: 'Beau temps, historique favorable, pas de jour férié. Journée à saisir.',
        },
      ],
      pricingFeatures: [
        'Application Fidmarket complète (accès illimité)',
        'Tablette fournie ou utilisation sur votre propre appareil',
        'Cartes de fidélité physiques incluses au démarrage',
        'Accompagnement à la mise en route sur votre marché',
        'Support réactif par téléphone ou message',
        'Mises à jour régulières incluses',
      ],
    }
  },
  computed: {
    doubledMarketItems() {
      return this.marketItems.concat(this.marketItems)
    }
  },
  methods: {
    closeMenu() { this.menuOpen = false }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

/* ── TOKENS ──────────────────────────────────────────────────────────────── */
.lp {
  --forest:    #1A3A2A;
  --forest-mid:#2D6A4F;
  --forest-lt: #D8F3DC;
  --cream:     #F5EDD6;
  --cream-dk:  #EDD9B0;
  --gold:      #E9C46A;
  --gold-dk:   #C8960A;
  --terra:     #E76F51;
  --slate:     #3D4F5C;
  --slate-lt:  #8A9BAB;
  --white:     #FFFFFF;
  --ink:       #1A2733;
  --bg:        #FAFAF7;
  --bg-alt:    #F2EDE4;
  --border:    #E5DDD0;

  --r-sm: 8px;
  --r-md: 14px;
  --r-lg: 20px;
  --r-xl: 28px;
  --max-w: 1100px;
  --gap: clamp(56px, 8vw, 96px);

  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 16px;
  line-height: 1.65;
  color: var(--ink);
  background: var(--bg);
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}
.lp *, .lp *::before, .lp *::after { box-sizing: border-box; }
.lp a { color: inherit; text-decoration: none; }
.lp ul { list-style: none; margin: 0; padding: 0; }
.lp section[id] { scroll-margin-top: 64px; }

.wrap {
  width: 100%;
  max-width: var(--max-w);
  margin-inline: auto;
  padding-inline: clamp(18px, 5vw, 56px);
}

/* ── BUTTONS ─────────────────────────────────────────────────────────────── */
.btn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 8px; padding: 11px 22px;
  border-radius: var(--r-md);
  font-family: 'DM Sans', sans-serif;
  font-size: 15px; font-weight: 600;
  cursor: pointer; border: none;
  text-decoration: none;
  transition: all .18s ease;
  white-space: nowrap;
}
.btn-lg  { padding: 14px 30px; font-size: 16px; border-radius: var(--r-lg); }
.btn-full { width: 100%; }

.lp .btn-primary { background: var(--forest); color: var(--cream); box-shadow: 0 2px 8px rgba(26,58,42,.25); }
.lp .btn-primary:hover { background: var(--forest-mid); color: var(--cream); transform: translateY(-1px); box-shadow: 0 4px 16px rgba(26,58,42,.3); }

.lp .btn-soft { background: var(--cream); color: var(--forest); border: 1.5px solid var(--cream-dk); }
.lp .btn-soft:hover { background: var(--cream-dk); color: var(--forest); }

.lp .btn-ghost { background: transparent; color: var(--ink); border: 1.5px solid var(--border); }
.lp .btn-ghost:hover { border-color: var(--forest); color: var(--forest); }

.lp .btn-gold { background: var(--gold); color: var(--forest); font-weight: 700; box-shadow: 0 2px 8px rgba(233,196,106,.35); }
.lp .btn-gold:hover { background: var(--gold-dk); color: var(--forest); transform: translateY(-1px); }

.lp .btn-ghost-white { background: transparent; color: var(--cream); border: 1.5px solid rgba(245,237,214,.65); }
.lp .btn-ghost-white:hover { background: rgba(245,237,214,.12); color: var(--cream); border-color: var(--cream); }

/* ── HEADER ──────────────────────────────────────────────────────────────── */
.hdr {
  position: sticky; top: 0; z-index: 100;
  background: rgba(245,237,214,.95);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--border);
}
.hdr__inner {
  max-width: var(--max-w); margin-inline: auto;
  padding-inline: clamp(18px, 5vw, 56px);
  display: flex; align-items: center; justify-content: space-between;
  height: 64px; gap: 16px; position: relative;
}
.hdr__logo { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.hdr__logo-mark { width: 36px; height: 36px; flex-shrink: 0; }
.hdr__logo-name { font-family: 'Playfair Display', Georgia, serif; font-size: 21px; font-weight: 700; color: var(--forest); }
.hdr__logo-name em { font-style: normal; color: var(--terra); }

.hdr__nav { display: flex; align-items: center; gap: 2px; }
.hdr__nav a { font-size: 14px; font-weight: 500; color: var(--slate); padding: 6px 14px; border-radius: var(--r-sm); transition: .15s; }
.hdr__nav a:hover { color: var(--forest); background: var(--bg-alt); }

.hdr__cta { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }

.hdr__burger { display: none; flex-direction: column; gap: 5px; background: none; border: none; cursor: pointer; padding: 6px; }
.hdr__burger span { display: block; width: 22px; height: 2px; background: var(--ink); border-radius: 2px; transition: .2s; }

/* ── MARKET STRIP (signature) ────────────────────────────────────────────── */
.market-strip {
  background: var(--forest);
  overflow: hidden;
  padding: 10px 0;
  border-bottom: 2px solid var(--gold);
}
.market-strip__track {
  display: flex;
  gap: 0;
  white-space: nowrap;
  animation: scroll-strip 35s linear infinite;
  width: max-content;
}
.market-strip__item {
  font-size: 13px;
  font-weight: 600;
  color: var(--cream);
  letter-spacing: 0.3px;
  padding: 0 28px;
  opacity: 0.85;
}
.market-strip__item:nth-child(even) { color: var(--gold); }
@keyframes scroll-strip {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

/* ── HERO ─────────────────────────────────────────────────────────────────── */
.hero {
  background: linear-gradient(160deg, var(--forest) 0%, #0F2A1A 100%);
  position: relative;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 60% at 80% 50%, rgba(233,196,106,.08) 0%, transparent 60%),
              radial-gradient(ellipse 40% 40% at 15% 80%, rgba(45,106,79,.3) 0%, transparent 60%);
  pointer-events: none;
}
.hero__body {
  position: relative; z-index: 1;
  max-width: var(--max-w); margin-inline: auto;
  padding-inline: clamp(18px, 5vw, 56px);
  padding-block: clamp(56px, 8vw, 96px);
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: clamp(40px, 6vw, 80px);
}
.hero__tag {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 20px;
}
.tag-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--terra); flex-shrink: 0; animation: pulse-dot 2s ease-in-out infinite; }
@keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }

.hero__title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(36px, 5vw, 58px);
  font-weight: 900;
  line-height: 1.1;
  color: var(--cream);
  margin: 0 0 20px;
  letter-spacing: -0.5px;
}
.hero__title-accent {
  color: var(--gold);
  font-style: italic;
}
.hero__sub {
  font-size: clamp(15px, 2vw, 17px);
  color: rgba(245,237,214,.7);
  max-width: 480px;
  margin: 0 0 32px;
  line-height: 1.75;
}
.hero__actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 40px; }

.hero__proof {
  display: flex; align-items: center; gap: 20px;
  padding: 20px 0 0;
  border-top: 1px solid rgba(245,237,214,.15);
}
.proof-item { text-align: left; }
.proof-num { display: block; font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 700; color: var(--gold); line-height: 1; }
.proof-label { font-size: 12px; color: rgba(245,237,214,.55); line-height: 1.4; margin-top: 4px; display: block; }
.proof-div { width: 1px; height: 36px; background: rgba(245,237,214,.2); flex-shrink: 0; }

/* Mockup */
.hero__mockup { position: relative; }
.mockup-card { background: var(--white); border-radius: var(--r-xl); overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,.35), 0 4px 16px rgba(0,0,0,.2); }
.mockup-header { background: #F0F0F0; padding: 10px 16px; display: flex; align-items: center; gap: 12px; }
.mockup-dots { display: flex; gap: 6px; }
.dot { width: 11px; height: 11px; border-radius: 50%; }
.dot--r { background: #FF5F57; }
.dot--y { background: #FEBC2E; }
.dot--g { background: #28C840; }
.mockup-url { font-size: 12px; color: #888; font-family: monospace; margin: 0 auto; }

.mockup-body { padding: 16px; }
.mkpi-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 14px; }
.mkpi { background: var(--bg); border-radius: var(--r-sm); padding: 10px 12px; }
.mkpi__label { font-size: 11px; color: var(--slate-lt); margin-bottom: 4px; }
.mkpi__val { font-family: 'Playfair Display', serif; font-size: 18px; font-weight: 700; color: var(--ink); }
.mkpi__val--green { color: var(--forest-mid); }
.mkpi__trend { font-size: 11px; color: var(--forest-mid); margin-top: 2px; }
.mkpi__trend--neutral { color: var(--slate-lt); }

.mforecast { background: linear-gradient(120deg, var(--forest), var(--forest-mid)); border-radius: var(--r-sm); padding: 12px 14px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.mforecast__label { font-size: 12px; color: rgba(245,237,214,.8); }
.mforecast__badge { font-size: 12px; font-weight: 700; color: var(--gold); white-space: nowrap; }

.mclient-list { display: flex; flex-direction: column; gap: 8px; }
.mclient { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: var(--r-sm); background: var(--bg); }
.mclient__avatar { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--white); flex-shrink: 0; }
.mclient__name { font-size: 13px; font-weight: 600; color: var(--ink); }
.mclient__pts { font-size: 11px; color: var(--slate-lt); }
.mclient__badge { margin-left: auto; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 20px; }
.badge-vip { background: #FEF3C7; color: #92400E; }
.badge-reg { background: var(--forest-lt); color: var(--forest); }
.badge-new { background: #EDE9FE; color: #5B21B6; }

/* Carte fidélité flottante */
.fid-card {
  position: absolute;
  bottom: -20px; right: -24px;
  width: 200px;
  background: linear-gradient(135deg, var(--gold) 0%, #D4A017 100%);
  border-radius: var(--r-lg);
  padding: 16px;
  box-shadow: 0 12px 32px rgba(0,0,0,.3);
  color: var(--forest);
}
.fid-card__brand { font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; opacity: .7; margin-bottom: 18px; }
.fid-card__name { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 700; margin-bottom: 12px; }
.fid-card__dots { display: flex; gap: 8px; margin-bottom: 8px; }
.fid-dot { width: 22px; height: 22px; border-radius: 50%; border: 2px solid rgba(26,58,42,.3); }
.fid-dot.filled { background: var(--forest); border-color: var(--forest); }
.fid-card__sub { font-size: 10px; opacity: .65; }

/* ── SECTIONS COMMUNES ───────────────────────────────────────────────────── */
.section { padding-block: var(--gap); }
.section-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 1.8px; text-transform: uppercase;
  color: var(--forest-mid); background: var(--forest-lt);
  padding: 4px 14px; border-radius: 20px; margin-bottom: 16px;
}
.section-eyebrow--light { color: var(--cream); background: rgba(245,237,214,.15); }
.section-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(28px, 4vw, 46px);
  font-weight: 700;
  line-height: 1.18;
  color: var(--ink);
  margin: 0 0 16px;
}
.section-title em { font-style: italic; color: var(--terra); }
.section-title--light { color: var(--cream); }
.section-title--light em { color: var(--gold); }
.section-title--center { text-align: center; }
.section-sub { font-size: clamp(15px, 2vw, 17px); color: var(--slate); max-width: 560px; line-height: 1.75; margin: 0; }
.section-sub--light { color: rgba(245,237,214,.65); }
.section-sub--center { text-align: center; margin-inline: auto; }

/* ── PROBLÈME ─────────────────────────────────────────────────────────────── */
.section-problem { background: var(--bg-alt); }
.problem-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin: 48px 0; }
.problem-item { background: var(--white); border-radius: var(--r-lg); padding: 24px; border: 1px solid var(--border); transition: box-shadow .2s; }
.problem-item:hover { box-shadow: 0 8px 24px rgba(26,58,42,.08); }
.problem-item__icon { font-size: 28px; margin-bottom: 14px; }
.problem-item__title { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 700; color: var(--ink); margin: 0 0 10px; }
.problem-item__text { font-size: 14px; color: var(--slate); line-height: 1.65; margin: 0; }

.problem-vs {
  display: flex; align-items: stretch; gap: 0;
  background: var(--white); border-radius: var(--r-xl); overflow: hidden;
  border: 1px solid var(--border); margin-top: 16px;
}
.vs-before, .vs-after { flex: 1; padding: 28px 32px; }
.vs-before { border-right: 1px solid var(--border); }
.vs-arrow { display: flex; align-items: center; justify-content: center; padding: 0 20px; font-size: 24px; color: var(--forest-mid); font-weight: 700; }
.vs-label { font-size: 11px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase; padding: 3px 12px; border-radius: 20px; display: inline-block; margin-bottom: 16px; }
.vs-label--before { background: #FEE2E2; color: #991B1B; }
.vs-label--after  { background: var(--forest-lt); color: var(--forest); }
.vs-before ul li, .vs-after ul li { font-size: 14px; color: var(--slate); padding: 5px 0; padding-left: 18px; position: relative; }
.vs-before ul li::before { content: '✗'; position: absolute; left: 0; color: #EF4444; font-size: 12px; }
.vs-after  ul li::before { content: '✓'; position: absolute; left: 0; color: var(--forest-mid); font-size: 12px; }

/* ── SOLUTION ─────────────────────────────────────────────────────────────── */
.section-solution { background: linear-gradient(160deg, var(--forest) 0%, #0F2A1A 100%); position: relative; overflow: hidden; }
.section-solution::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 50% 60% at 90% 50%, rgba(233,196,106,.06) 0%, transparent 60%);
  pointer-events: none;
}
.section-solution .wrap { position: relative; z-index: 1; }
.features-list { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 52px; }
.feat { background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: var(--r-lg); padding: 28px; transition: background .2s, border-color .2s; }
.feat:hover { background: rgba(255,255,255,.08); border-color: rgba(233,196,106,.3); }
.feat__num { font-family: 'Playfair Display', serif; font-size: 13px; font-weight: 700; color: var(--gold); opacity: .5; margin-bottom: 14px; }
.feat__icon { font-size: 28px; margin-bottom: 12px; }
.feat__title { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; color: var(--cream); margin: 0 0 10px; }
.feat__text { font-size: 14px; color: rgba(245,237,214,.6); line-height: 1.65; margin: 0; }

/* ── POUR QUI ─────────────────────────────────────────────────────────────── */
.section-who { background: var(--bg); }
.etalage {
  display: flex; flex-wrap: wrap; gap: 12px;
  margin: 40px 0 48px;
}
.etal {
  display: flex; align-items: center; gap: 8px;
  background: var(--white); border: 1px solid var(--border);
  border-radius: 99px; padding: 8px 18px;
  font-size: 14px; font-weight: 500; color: var(--ink);
  transition: all .15s;
  cursor: default;
}
.etal:hover { background: var(--forest-lt); border-color: var(--forest-mid); color: var(--forest); transform: translateY(-2px); }
.etal__emoji { font-size: 18px; }

.temoignage {
  background: var(--cream); border-left: 4px solid var(--gold);
  border-radius: 0 var(--r-lg) var(--r-lg) 0;
  padding: 28px 32px; margin: 0;
}
.temoignage__text { font-family: 'Playfair Display', serif; font-size: clamp(17px, 2.5vw, 22px); font-style: italic; color: var(--ink); margin: 0 0 16px; line-height: 1.6; }
.temoignage__footer { display: flex; align-items: center; gap: 10px; }
.temoignage__name { font-weight: 700; font-size: 14px; color: var(--forest); }
.temoignage__role { font-size: 13px; color: var(--slate); }
.temoignage__role::before { content: '·'; margin-right: 10px; }

/* ── PRÉVISIONS ───────────────────────────────────────────────────────────── */
.section-forecast { background: var(--bg-alt); }
.forecast-cards { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; margin: 48px 0 20px; }
.fc-card { background: var(--white); border-radius: var(--r-lg); padding: 28px; border: 1px solid var(--border); position: relative; overflow: hidden; }
.fc-card__indicator { position: absolute; top: 0; left: 0; right: 0; height: 4px; border-radius: var(--r-lg) var(--r-lg) 0 0; }
.fc-card--low  .fc-card__indicator { background: #F87171; }
.fc-card--mid  .fc-card__indicator { background: var(--gold); }
.fc-card--high .fc-card__indicator { background: var(--forest-mid); }
.fc-card__tag { font-size: 11px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; margin-bottom: 16px; margin-top: 8px; }
.fc-card--low  .fc-card__tag { color: #DC2626; }
.fc-card--mid  .fc-card__tag { color: var(--gold-dk); }
.fc-card--high .fc-card__tag { color: var(--forest-mid); }
.fc-card__icon { font-size: 28px; margin-bottom: 12px; }
.fc-card__title { font-family: 'Playfair Display', serif; font-size: 18px; font-weight: 700; color: var(--ink); margin: 0 0 10px; }
.fc-card__text { font-size: 14px; color: var(--slate); line-height: 1.65; margin: 0; }
.forecast-disclaimer { font-size: 12px; color: var(--slate-lt); font-style: italic; }

/* ── TARIFS ───────────────────────────────────────────────────────────────── */
.section-pricing { background: var(--bg); }
.pricing-card {
  max-width: 560px; margin-inline: auto;
  background: var(--white); border-radius: var(--r-xl);
  padding: clamp(32px, 5vw, 56px);
  border: 1px solid var(--border);
  box-shadow: 0 12px 40px rgba(26,58,42,.08);
  margin-top: 40px;
}
.pricing-card__badge { font-size: 13px; font-weight: 700; color: var(--terra); margin-bottom: 16px; }
.pricing-card__title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: var(--ink); margin: 0 0 10px; }
.pricing-card__desc { font-size: 15px; color: var(--slate); margin: 0 0 28px; }
.pricing-features { display: flex; flex-direction: column; gap: 12px; margin: 0 0 32px; }
.pricing-features li { font-size: 15px; color: var(--ink); font-weight: 500; padding-left: 26px; position: relative; }
.pricing-features li::before { content: ''; position: absolute; left: 0; top: 2px; width: 18px; height: 18px; border-radius: 50%; background: url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='9' cy='9' r='9' fill='%23D8F3DC'/%3E%3Cpath d='M5 9l3 3 5-5' stroke='%232D6A4F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat center / contain; }
.pricing-note { font-size: 13px; color: var(--slate-lt); text-align: center; margin: 16px 0 0; border-top: 1px solid var(--border); padding-top: 16px; }

/* ── CONTACT ──────────────────────────────────────────────────────────────── */
.section-contact { background: var(--bg-alt); }
.cta-block {
  background: linear-gradient(145deg, var(--forest) 0%, #0F2A1A 100%);
  border-radius: var(--r-xl); padding: clamp(48px, 7vw, 80px);
  text-align: center; position: relative; overflow: hidden;
}
.cta-block::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 60% 60% at 50% 110%, rgba(233,196,106,.15) 0%, transparent 65%);
  pointer-events: none;
}
.cta-deco { font-size: 48px; margin-bottom: 16px; position: relative; }
.cta-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(26px, 4vw, 42px);
  font-weight: 700; font-style: italic;
  color: var(--cream); margin: 0 0 16px; position: relative; line-height: 1.25;
}
.cta-sub { font-size: 16px; color: rgba(245,237,214,.6); margin: 0 auto 36px; max-width: 440px; position: relative; line-height: 1.7; }
.cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; margin-bottom: 24px; }
.cta-contact-info { position: relative; display: flex; gap: 12px; align-items: center; justify-content: center; font-size: 14px; color: rgba(245,237,214,.45); }
.cta-contact-info a { color: rgba(245,237,214,.6); transition: color .15s; }
.cta-contact-info a:hover { color: var(--gold); }

/* ── FOOTER ───────────────────────────────────────────────────────────────── */
.footer { background: #0C1F14; padding-block: 48px 28px; color: rgba(245,237,214,.45); }
.footer__inner {
  max-width: var(--max-w); margin-inline: auto;
  padding-inline: clamp(18px, 5vw, 56px);
  display: grid; grid-template-columns: 2fr 1fr 1fr;
  gap: 40px; margin-bottom: 40px;
}
.footer__brand-name { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; color: var(--cream); margin-bottom: 10px; }
.footer__brand-name em { font-style: normal; color: var(--terra); }
.footer__brand-tag { font-size: 13px; line-height: 1.65; max-width: 260px; margin: 0; }
.footer__col-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: rgba(245,237,214,.35); margin-bottom: 16px; }
.footer__links { display: flex; flex-direction: column; gap: 9px; }
.footer__links a { font-size: 13px; color: rgba(245,237,214,.4); transition: color .15s; }
.footer__links a:hover { color: var(--cream); }
.footer__bottom {
  max-width: var(--max-w); margin-inline: auto;
  padding-inline: clamp(18px, 5vw, 56px);
  border-top: 1px solid rgba(245,237,214,.08);
  padding-top: 22px; display: flex;
  align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
}
.footer__bottom nav { display: flex; gap: 20px; }
.footer__bottom nav a { font-size: 12px; color: rgba(245,237,214,.3); transition: color .15s; }
.footer__bottom nav a:hover { color: rgba(245,237,214,.7); }
.footer__bottom span { font-size: 12px; }

/* ── RESPONSIVE ───────────────────────────────────────────────────────────── */
@media (max-width: 960px) {
  .hero__body { grid-template-columns: 1fr; }
  .hero__mockup { display: none; }
  .problem-grid { grid-template-columns: 1fr 1fr; }
  .features-list { grid-template-columns: 1fr 1fr; }
  .hdr__nav { display: none; }
  .hdr__nav.open {
    display: flex; flex-direction: column; align-items: stretch;
    position: absolute; top: 64px; left: 0; right: 0;
    background: rgba(245,237,214,.98); border-bottom: 1px solid var(--border);
    padding: 12px; gap: 4px; box-shadow: 0 8px 24px rgba(0,0,0,.08);
  }
  .hdr__burger { display: flex; }
  .footer__inner { grid-template-columns: 1fr 1fr; }
  .vs-arrow { display: none; }
  .problem-vs { flex-direction: column; }
  .vs-before { border-right: none; border-bottom: 1px solid var(--border); }
}
@media (max-width: 640px) {
  .hdr__inner { gap: 10px; }
  .hdr__logo-name { font-size: 18px; }
  .hdr__cta { flex-shrink: 1; min-width: 0; }
  .hdr__cta .btn { padding: 8px 12px; font-size: 14px; min-height: 40px; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
  .hdr__cta .btn-ghost { display: none; }
  .hdr__burger { flex-shrink: 0; }
  .features-list { grid-template-columns: 1fr; }
  .forecast-cards { grid-template-columns: 1fr; }
  .problem-grid { grid-template-columns: 1fr; }
  .footer__inner { grid-template-columns: 1fr; }
  .footer__bottom { flex-direction: column; text-align: center; }
  .footer__bottom nav { flex-wrap: wrap; justify-content: center; }
  .hero__actions { flex-direction: column; align-items: flex-start; }
  .hero__proof { gap: 14px; }
  .proof-num { font-size: 22px; }
}
@media (prefers-reduced-motion: reduce) {
  .market-strip__track { animation: none; }
  .tag-dot { animation: none; }
}
</style>
